import {createImageUrlBuilder} from '@sanity/image-url';

import type {Image, ImageWeb} from '@/modules/foundation/components/image/types';
import {dataset, projectId} from '@/sanity/lib/api';
import type {
	ImageWebFull,
	ImageWebSimple,
	internalGroqTypeReferenceTo,
	SanityImageCrop,
	SanityImageHotspot,
} from '@/sanity/schemas/types';

const imageUrlsBuilder = createImageUrlBuilder({
	dataset: dataset,
	projectId: projectId,
});

export type SanityImage = {
	asset?: {
		_ref: string;
		_type: 'reference';
		_weak?: boolean;
		[internalGroqTypeReferenceTo]?: 'sanity.imageAsset';
	};
	hotspot?: SanityImageHotspot;
	crop?: SanityImageCrop;
};

export const imageFromSanityImage = (
	image?: SanityImage | null,
	aspectRatio?: number,
): Image | null => {
	if (!image?.asset?._ref) {
		return null;
	}
	// First find the natural width and height from the asset ID. This
	// seems a bit hacky, but it's the same thing next-sanity-image
	// does, so it's likely to be the correct way of doing it. If we
	// couldn't parse the size form the ID we would have to do another
	// request to fetch the asset document, which would be annoying.
	const dimensions = image.asset._ref.split('-')[2];
	let [width, height] = dimensions.split('x').map((num) => parseInt(num, 10));

	// If the image has been cropped, adjust the width and height to
	// the cropped size:
	const crop = image.crop;
	if (crop) {
		width = Math.round(width * (1 - ((crop.left ?? 0) + (crop.right ?? 0))));
		height = Math.round(height * (1 - ((crop.top ?? 0) + (crop.bottom ?? 0))));
	}

	// Resolve the hotspot of the image, taking into consideration the
	// crop rectangle if one is set:
	let hotspot: {x: number; y: number} | undefined;
	if (image.hotspot) {
		if (crop) {
			hotspot = {
				x:
					((image.hotspot.x ?? 0) - (crop.left ?? 0)) /
					(1 - (crop.left ?? 0) - (crop.right ?? 0)),
				y:
					((image.hotspot.y ?? 0) - (crop.top ?? 0)) /
					(1 - (crop.top ?? 0) - (crop.bottom ?? 0)),
			};
		} else {
			hotspot = {
				x: image.hotspot.x ?? 0,
				y: image.hotspot.y ?? 0,
			};
		}
	}

	// If the user asked for a different aspect ratio, shrink either
	// the height or width depending on whether the image should be
	// wider or more narrow than it naturally is:
	if (aspectRatio) {
		const naturalAspectRatio = width / height;

		if (aspectRatio > naturalAspectRatio) {
			height = Math.round(width / aspectRatio);
		} else {
			width = Math.round(height * aspectRatio);
		}
	}

	const src = imageUrlsBuilder.image(image).width(width).height(height).url();

	return {
		src,
		width,
		height,
		hotspot,
	};
};

export const imageWebFromImageWebSchema = (
	image?: ImageWebFull | ImageWebSimple,
	aspectRatio?: number,
): ImageWeb | null => {
	const img = imageFromSanityImage(image, aspectRatio);

	if (!image || !img) {
		return null;
	}

	if (image._type === 'imageWebFull') {
		return {
			image: img,
			altText: image.altText,
			credit: image.credit,
			caption: image.caption,
		};
	} else {
		return {
			image: img,
			altText: image.altText,
		};
	}
};
